import React, { forwardRef } from 'react';
type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}
const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-gradient-to-r from-[#1BACA6] to-[#1890C8] text-white hover:from-[#159A94] hover:to-[#1580B6] focus:ring-[#1BACA6] shadow-sm',
  secondary: 'bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-500 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600',
  danger: 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-500 shadow-sm',
  ghost: 'text-gray-700 hover:bg-gray-100 focus:ring-gray-500 dark:text-gray-300 dark:hover:bg-gray-800',
  outline: 'border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-[#1BACA6] dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800'
};
const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base'
};
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  className = '',
  disabled,
  children,
  ...props
}, ref) => {
  return <button ref={ref} disabled={disabled || isLoading} className={`
        inline-flex items-center justify-center font-medium rounded-lg
        transition-all duration-150 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-offset-2
        disabled:opacity-50 disabled:cursor-not-allowed
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${className}
      `} {...props} data-id="element-4558">
        {isLoading ? <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" data-id="element-4559">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-4560" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-4561" />
          </svg> : leftIcon ? <span className="mr-2" data-id="element-4562">{leftIcon}</span> : null}
        {children}
        {rightIcon && !isLoading && <span className="ml-2" data-id="element-4563">{rightIcon}</span>}
      </button>;
});
Button.displayName = 'Button';